import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import useAuthStore from '../hooks/useAuthStore';

const streams = ['ICSE Class 9', 'ICSE Class 10', 'ISC Class 11', 'ISC Class 12'];

function Login() {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    stream: streams[0],
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const { login, signup } = useAuthStore();

  const validateForm = () => {
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Invalid email address';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (!isLogin) {
      if (!formData.name) {
        newErrors.name = 'Name is required';
      }
      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      if (isLogin) {
        const result = await login(formData.email, formData.password);
        if (!result.success) {
          setErrors({ general: result.error });
        }
      } else {
        const result = await signup(formData.name, formData.email, formData.password, formData.stream);
        if (!result.success) {
          setErrors({ general: result.error });
        }
      }
    } catch (error) {
      setErrors({ general: 'An error occurred. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: '' });
    }
  };

  return (
    <div 
      style={{ 
        width: '100vw', 
        height: '100vh', 
        background: 'var(--color-void)', 
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden'
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        style={{ width: '100%', maxWidth: '450px', padding: '2rem' }}
      >
        <div 
          style={{ 
            background: '#111111', 
            backdropFilter: 'blur(12px)', 
            borderRadius: 'var(--radius-cards)', 
            padding: 'var(--card-padding)', 
            border: '1px solid rgba(255, 255, 255, 0.05)' 
          }}
        >
          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <h2 
              style={{ 
                fontSize: 'var(--text-heading)', 
                fontWeight: 'var(--font-weight-semibold)', 
                color: 'var(--color-bone)', 
                marginBottom: '0.5rem' 
              }}
            >
              {isLogin ? 'Welcome Back' : 'Create Account'}
            </h2>
            <p style={{ color: 'var(--color-smoke)', fontSize: 'var(--text-body-sm)' }}>
              {isLogin ? 'Sign in to continue to Solve' : 'Join thousands of students on Solve'}
            </p>
          </div>

          <div 
            style={{ 
              display: 'flex', 
              marginBottom: '2rem', 
              background: 'rgba(0, 0, 0, 0.3)', 
              borderRadius: 'var(--radius-buttons)', 
              padding: '4px' 
            }}
          >
            <button
              onClick={() => setIsLogin(true)}
              style={{
                flex: 1,
                padding: '12px 16px',
                borderRadius: 'var(--radius-buttons)',
                fontWeight: 'var(--font-weight-semibold)',
                transition: 'all 0.3s',
                background: isLogin ? 'var(--color-plum-voltage)' : 'transparent',
                color: isLogin ? 'var(--color-bone)' : 'var(--color-smoke)',
                border: 'none',
                cursor: 'pointer',
                fontSize: 'var(--text-caption)',
                letterSpacing: 'var(--tracking-caption)',
                textTransform: 'uppercase'
              }}
            >
              Sign In
            </button>
            <button
              onClick={() => setIsLogin(false)}
              style={{
                flex: 1,
                padding: '12px 16px',
                borderRadius: 'var(--radius-buttons)',
                fontWeight: 'var(--font-weight-semibold)',
                transition: 'all 0.3s',
                background: !isLogin ? 'var(--color-plum-voltage)' : 'transparent',
                color: !isLogin ? 'var(--color-bone)' : 'var(--color-smoke)',
                border: 'none',
                cursor: 'pointer',
                fontSize: 'var(--text-caption)',
                letterSpacing: 'var(--tracking-caption)',
                textTransform: 'uppercase'
              }}
            >
              Create Account
            </button>
          </div>

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            {!isLogin && (
              <AnimatePresence>
                {!isLogin && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                  >
                    <div>
                      <label 
                        style={{ 
                          display: 'block', 
                          fontSize: 'var(--text-caption)', 
                          fontWeight: 'var(--font-weight-semibold)', 
                          color: 'var(--color-bone)', 
                          marginBottom: '0.5rem', 
                          letterSpacing: 'var(--tracking-caption)', 
                          textTransform: 'uppercase' 
                        }}
                      >
                        Full Name
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        style={{
                          width: '100%',
                          padding: '14px 16px',
                          background: 'var(--color-void)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          borderRadius: 'var(--radius-buttons)',
                          color: 'var(--color-bone)',
                          fontSize: 'var(--text-body-sm)',
                          outline: 'none',
                          transition: 'all 0.3s'
                        }}
                        placeholder="Enter your full name"
                      />
                      {errors.name && <p style={{ color: '#f87171', fontSize: 'var(--text-caption)', marginTop: '0.25rem' }}>{errors.name}</p>}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            )}

            <div>
              <label 
                style={{ 
                  display: 'block', 
                  fontSize: 'var(--text-caption)', 
                  fontWeight: 'var(--font-weight-semibold)', 
                  color: 'var(--color-bone)', 
                  marginBottom: '0.5rem', 
                  letterSpacing: 'var(--tracking-caption)', 
                  textTransform: 'uppercase' 
                }}
              >
                Email Address
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  background: 'var(--color-void)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: 'var(--radius-buttons)',
                  color: 'var(--color-bone)',
                  fontSize: 'var(--text-body-sm)',
                  outline: 'none',
                  transition: 'all 0.3s'
                }}
                placeholder="Enter your email"
              />
              {errors.email && <p style={{ color: '#f87171', fontSize: 'var(--text-caption)', marginTop: '0.25rem' }}>{errors.email}</p>}
            </div>

            <div>
              <label 
                style={{ 
                  display: 'block', 
                  fontSize: 'var(--text-caption)', 
                  fontWeight: 'var(--font-weight-semibold)', 
                  color: 'var(--color-bone)', 
                  marginBottom: '0.5rem', 
                  letterSpacing: 'var(--tracking-caption)', 
                  textTransform: 'uppercase' 
                }}
              >
                Password
              </label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  background: 'var(--color-void)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: 'var(--radius-buttons)',
                  color: 'var(--color-bone)',
                  fontSize: 'var(--text-body-sm)',
                  outline: 'none',
                  transition: 'all 0.3s'
                }}
                placeholder="Min 6 characters"
              />
              {errors.password && <p style={{ color: '#f87171', fontSize: 'var(--text-caption)', marginTop: '0.25rem' }}>{errors.password}</p>}
            </div>

            {!isLogin && (
              <AnimatePresence>
                {!isLogin && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                  >
                    <div>
                      <label 
                        style={{ 
                          display: 'block', 
                          fontSize: 'var(--text-caption)', 
                          fontWeight: 'var(--font-weight-semibold)', 
                          color: 'var(--color-bone)', 
                          marginBottom: '0.5rem', 
                          letterSpacing: 'var(--tracking-caption)', 
                          textTransform: 'uppercase' 
                        }}
                      >
                        Confirm Password
                      </label>
                      <input
                        type="password"
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        style={{
                          width: '100%',
                          padding: '14px 16px',
                          background: 'var(--color-void)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          borderRadius: 'var(--radius-buttons)',
                          color: 'var(--color-bone)',
                          fontSize: 'var(--text-body-sm)',
                          outline: 'none',
                          transition: 'all 0.3s'
                        }}
                        placeholder="Confirm your password"
                      />
                      {errors.confirmPassword && <p style={{ color: '#f87171', fontSize: 'var(--text-caption)', marginTop: '0.25rem' }}>{errors.confirmPassword}</p>}
                    </div>

                    <div>
                      <label 
                        style={{ 
                          display: 'block', 
                          fontSize: 'var(--text-caption)', 
                          fontWeight: 'var(--font-weight-semibold)', 
                          color: 'var(--color-bone)', 
                          marginBottom: '0.5rem', 
                          letterSpacing: 'var(--tracking-caption)', 
                          textTransform: 'uppercase' 
                        }}
                      >
                        Target Stream
                      </label>
                      <select
                        name="stream"
                        value={formData.stream}
                        onChange={handleChange}
                        style={{
                          width: '100%',
                          padding: '14px 16px',
                          background: 'var(--color-void)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          borderRadius: 'var(--radius-buttons)',
                          color: 'var(--color-bone)',
                          fontSize: 'var(--text-body-sm)',
                          outline: 'none',
                          transition: 'all 0.3s'
                        }}
                      >
                        {streams.map((stream) => (
                          <option key={stream} value={stream} style={{ background: 'var(--color-void)' }}>
                            {stream}
                          </option>
                        ))}
                      </select>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            )}

            {errors.general && (
              <div 
                style={{ 
                  background: 'rgba(248, 113, 113, 0.1)', 
                  border: '1px solid rgba(248, 113, 113, 0.3)', 
                  borderRadius: 'var(--radius-buttons)', 
                  padding: '1rem' 
                }}
              >
                <p style={{ color: '#f87171', fontSize: 'var(--text-caption)', textAlign: 'center' }}>{errors.general}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: '14px 16px',
                background: 'var(--color-plum-voltage)',
                color: 'var(--color-bone)',
                fontWeight: 'var(--font-weight-semibold)',
                borderRadius: 'var(--radius-buttons)',
                border: 'none',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s',
                opacity: loading ? 0.5 : 1,
                fontSize: 'var(--text-caption)',
                letterSpacing: 'var(--tracking-caption)',
                textTransform: 'uppercase'
              }}
            >
              {loading ? (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg 
                    style={{ animation: 'spin 1s linear infinite', height: '20px', width: '20px', marginRight: '8px' }} 
                    viewBox="0 0 24 24"
                  >
                    <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Processing...
                </span>
              ) : isLogin ? (
                'Sign In'
              ) : (
                'Create Account'
              )}
            </button>
          </form>

          <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>
            <p style={{ color: 'var(--color-smoke)', fontSize: 'var(--text-body-sm)' }}>
              {isLogin ? "Don't have an account? " : 'Already have an account? '}
              <button
                onClick={() => setIsLogin(!isLogin)}
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  color: 'var(--color-plum-voltage)', 
                  fontWeight: 'var(--font-weight-semibold)', 
                  cursor: 'pointer', 
                  fontSize: 'var(--text-body-sm)' 
                }}
              >
                {isLogin ? 'Sign up' : 'Sign in'}
              </button>
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

export default Login;
